// Phase I5.5 — Direction C (Bloomberg / terminal density).
//
// Dense grid layout: command bar + oversized fixture banner, then a
// two-column row with legs+verdict cells on the left and Agent Debate
// timeline foregrounded on the right. Below, a single-column terminal
// strip stacks the v1.0 panels (MarketCategorySelector, ScorelineMatrix,
// AdversarialReview, BuildYourOwn) at terminal density. Composes the
// existing 12 panels — no new hooks, no fabricated data.

import { useMemo, useState } from "react";
import { useConversionTriggerPump } from "../../free-tier/useConversionTriggerPump.ts";
import { useSuperAccaToday } from "../../super-acca/useSuperAccaToday.ts";
import { AdversarialReview } from "./panels/AdversarialReview.tsx";
import { AgentDebate } from "./panels/AgentDebate.tsx";
import { BuildYourOwn } from "./panels/BuildYourOwn.tsx";
import { CornersIntelPanel } from "./panels/CornersIntelPanel.tsx";
import { IntelligenceVerdict } from "./panels/IntelligenceVerdict.tsx";
import { MarketCategorySelector } from "./panels/MarketCategorySelector.tsx";
import type { MarketCategoryFilter } from "./panels/MarketCategorySelector.tsx";
import { PlayerDNAPanel } from "./panels/PlayerDNAPanel.tsx";
import { RefereeDNAPanel } from "./panels/RefereeDNAPanel.tsx";
import { ScorelineMatrix } from "./panels/ScorelineMatrix.tsx";
import { TierStrip } from "./panels/TierStrip.tsx";

const LEG_COLS = ["#", "MARKET", "SEL", "MODEL", "MKT", "EDGE", "ODDS", "CONF"] as const;
const tableStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 14,
  borderCollapse: "collapse",
  fontSize: 11.5,
  fontFamily: "var(--sa-font-mono)",
};
const thStyle: React.CSSProperties = {
  padding: "8px 6px",
  borderBottom: "1px solid var(--sa-hairline)",
  fontSize: 9.5,
  letterSpacing: "0.18em",
  fontWeight: 500,
};
const tdStyle: React.CSSProperties = { padding: "12px 6px" };
const cmdBarStyle: React.CSSProperties = {
  background: "#000",
  borderBottom: "1px solid var(--sa-hairline)",
  padding: "10px 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};
const bannerStyle: React.CSSProperties = {
  padding: "32px 32px 18px",
  borderBottom: "1px solid var(--sa-hairline)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 24,
  flexWrap: "wrap",
};
const bannerTitleStyle: React.CSSProperties = {
  fontFamily: "var(--sa-font-display)",
  fontSize: 72,
  lineHeight: 0.9,
  marginTop: 8,
  letterSpacing: "0.02em",
  color: "var(--sa-fg-primary)",
};
const bannerOddsStyle: React.CSSProperties = {
  fontFamily: "var(--sa-font-display)",
  fontSize: 64,
  color: "var(--sa-accent-saffron)",
  lineHeight: 1,
};

const eyebrow: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono)",
  fontSize: 10,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--sa-accent-saffron)",
};

function PanelHead({ title, right }: { title: string; right?: string }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid var(--sa-hairline)",
        paddingBottom: 8,
      }}
    >
      <span style={eyebrow}>{title}</span>
      {right ? (
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: "var(--sa-fg-secondary)",
            letterSpacing: "0.18em",
          }}
        >
          {right}
        </span>
      ) : null}
    </div>
  );
}

export function SuperAccaPageDirectionC(): JSX.Element {
  const vm = useSuperAccaToday();
  const {
    slip,
    fixture,
    disciplinary,
    territorial,
    playerPerformance,
    scorelineMatrix,
    derivedMarkets,
    sharpMoney,
    loading,
    error,
  } = vm;

  const [category, setCategory] = useState<MarketCategoryFilter>("ALL");

  useConversionTriggerPump(
    slip ? "super_acca_view" : null,
    slip ? { slip_id: slip.id, pick_date: slip.pick_date } : {},
  );

  const kickoff = useMemo(() => {
    if (!fixture?.kickoff_at) return null;
    try {
      return new Date(fixture.kickoff_at).toUTCString().slice(17, 22) + " GMT";
    } catch {
      return null;
    }
  }, [fixture]);

  return (
    <div
      className="sa-root"
      data-screen-label="00 SUPER ACCA · C"
      style={{
        background: "var(--sa-bg-base)",
        minHeight: "var(--vh-full)",
        color: "var(--sa-fg-primary)",
        fontFamily: "var(--sa-font-mono)",
        fontSize: 12,
      }}
    >
      {/* Command bar */}
      <div style={cmdBarStyle}>
        <div style={{ display: "flex", gap: 22, alignItems: "center" }}>
          <span style={{ color: "var(--sa-accent-saffron)", fontSize: 10, letterSpacing: "0.2em" }}>
            SUPER_ACCA · BET_BUILDER · DIRECTION C
          </span>
          {slip?.status === "published" ? (
            <span style={{ color: "var(--sa-signal-green)", fontSize: 10, letterSpacing: "0.16em" }}>● PUBLISHED</span>
          ) : slip?.status === "abstained" ? (
            <span style={{ color: "var(--sa-signal-red)", fontSize: 10, letterSpacing: "0.16em" }}>● ABSTAINED</span>
          ) : null}
          {fixture?.league ? (
            <span style={{ fontSize: 10.5 }}>
              {fixture.league}
              {kickoff ? ` · ${kickoff}` : ""}
            </span>
          ) : null}
        </div>
      </div>

      {/* Big banner */}
      {fixture ? (
        <div style={bannerStyle}>
          <div>
            <div style={eyebrow}>
              {(fixture.competition ?? fixture.league ?? "FIXTURE").toString().toUpperCase()} · ID{" "}
              {fixture.fixture_id.slice(0, 6).toUpperCase()}
            </div>
            <div style={bannerTitleStyle}>
              {fixture.home.toUpperCase()}{" "}
              <span style={{ color: "var(--sa-fg-muted)" }}>VS</span>{" "}
              {fixture.away.toUpperCase()}
            </div>
          </div>
          {slip ? (
            <div style={{ textAlign: "right" }}>
              <div style={bannerOddsStyle}>{slip.combined_odds.toFixed(2)}×</div>
              <div style={{ fontSize: 10, color: "var(--sa-fg-muted)", letterSpacing: "0.18em", marginTop: 4 }}>
                COMBINED · {slip.legs.length} LEGS
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            margin: "16px 24px 0",
            padding: 12,
            border: "1px solid var(--sa-signal-red)",
            background: "var(--sa-signal-red-dim)",
            color: "var(--sa-signal-red)",
            fontSize: 11,
            letterSpacing: "0.12em",
          }}
        >
          ERROR · {error}
        </div>
      ) : null}

      {loading ? (
        <div
          style={{
            margin: 24,
            padding: 32,
            border: "1px solid var(--sa-hairline)",
            background: "var(--sa-bg-panel)",
            color: "var(--sa-fg-secondary)",
            letterSpacing: "0.16em",
          }}
        >
          LOADING TERMINAL…
        </div>
      ) : !slip ? (
        <EmptyC />
      ) : (
        <>
          {/* Grid: legs left, debate timeline right */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
              gap: 1,
              background: "var(--sa-hairline)",
              borderBottom: "1px solid var(--sa-hairline)",
            }}
          >
            {/* LEFT: legs table + verdict cells */}
            <div style={{ background: "var(--sa-bg-base)", padding: 22, minWidth: 0 }}>
              <PanelHead title="BET_BUILDER / LEGS" right={`CONF ${slip.confidence ?? "—"}/100`} />
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ color: "var(--sa-fg-muted)", textAlign: "left" }}>
                      {LEG_COLS.map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slip.legs.map((leg, i) => {
                      const confColor =
                        leg.legConfidence != null && leg.legConfidence >= 75
                          ? "var(--sa-signal-green)"
                          : "var(--sa-accent-saffron)";
                      return (
                        <tr
                          key={i}
                          style={{
                            background: leg.anchor ? "rgba(212,168,67,0.05)" : "transparent",
                            borderBottom: "1px solid var(--sa-hairline)",
                          }}
                        >
                          <td style={{ ...tdStyle, color: leg.anchor ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)" }}>
                            {leg.anchor ? "◆" : "○"} L{String(i + 1).padStart(2, "0")}
                          </td>
                          <td style={{ ...tdStyle, color: "var(--sa-fg-primary)" }}>{leg.market}</td>
                          <td style={{ ...tdStyle, color: "var(--sa-accent-saffron)" }}>{leg.selection.toUpperCase()}</td>
                          <td style={tdStyle}>{leg.model_prob}%</td>
                          <td style={{ ...tdStyle, color: "var(--sa-fg-muted)" }}>{leg.market_prob}%</td>
                          <td style={{ ...tdStyle, color: "var(--sa-accent-saffron)" }}>+{leg.edge_pct}pp</td>
                          <td style={tdStyle}>{leg.odds.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: confColor }}>{leg.legConfidence ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Verdict cells */}
              <div style={{ marginTop: 22 }}>
                <TierStrip slip={slip} />
                <div style={{ marginTop: 14 }}>
                  <IntelligenceVerdict slip={slip} />
                </div>
              </div>

              {/* Line drift cell */}
              <div
                style={{
                  marginTop: 18,
                  padding: 14,
                  background: "var(--sa-bg-panel)",
                  border: "1px solid var(--sa-hairline)",
                  borderRadius: 4,
                }}
              >
                <PanelHead title="LINE_DRIFT" right={sharpMoney.signal ?? "—"} />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 10,
                    fontFamily: "var(--sa-font-mono, JetBrains Mono, monospace)",
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: "var(--sa-fg-muted)" }}>
                    {sharpMoney.open != null ? sharpMoney.open.toFixed(2) : "—"}
                  </span>
                  <span style={{ color: "var(--sa-accent-saffron)" }}>→</span>
                  <span style={{ color: "var(--sa-accent-saffron)" }}>
                    {sharpMoney.now != null ? sharpMoney.now.toFixed(2) : "—"}
                  </span>
                  {sharpMoney.drift_pp != null ? (
                    <span style={{ marginLeft: "auto", color: "var(--sa-fg-secondary)", fontSize: 11 }}>
                      Δ {sharpMoney.drift_pp > 0 ? "+" : ""}
                      {sharpMoney.drift_pp}pp
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* RIGHT: foregrounded debate timeline */}
            <div
              style={{
                background: "var(--sa-bg-base)",
                padding: 22,
                minWidth: 0,
                position: "relative",
              }}
            >
              <PanelHead title="AGENT_DEBATE" right={`${slip.agent_debate.length} VOICES`} />
              <div style={{ marginTop: 12 }}>
                <AgentDebate slip={slip} defaultOpen />
              </div>
            </div>
          </div>

          {/* v1.0 panels — terminal-density single column */}
          <div
            style={{
              padding: 22,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
              background: "var(--sa-hairline)",
            }}
          >
            <div style={{ background: "var(--sa-bg-base)", padding: 18 }}>
              <MarketCategorySelector active={category} onChange={setCategory} />
              <div style={{ marginTop: 14 }}>
                {category === "DISCIPLINE" ? <RefereeDNAPanel data={disciplinary} /> : null}
                {category === "TERRITORY" ? <CornersIntelPanel data={territorial} /> : null}
                {category === "PLAYER" ? <PlayerDNAPanel data={playerPerformance} /> : null}
              </div>
            </div>
            <div style={{ background: "var(--sa-bg-base)", padding: 18 }}>
              <ScorelineMatrix matrix={scorelineMatrix} derived={derivedMarkets} />
            </div>
            <div style={{ background: "var(--sa-bg-base)", padding: 18 }}>
              <AdversarialReview slip={slip} />
            </div>
            <div style={{ background: "var(--sa-bg-base)", padding: 18 }}>
              <BuildYourOwn slip={slip} />
            </div>
          </div>
        </>
      )}

      <footer
        style={{
          padding: "16px 24px",
          borderTop: "1px solid var(--sa-hairline)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10.5,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.18em",
          textAlign: "center",
        }}
      >
        STRIKZA NEVER PLACES BETS · COPY ANY SLIP MANUALLY
      </footer>
    </div>
  );
}

function EmptyC() {
  return (
    <div
      style={{
        margin: 24,
        padding: 32,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
      }}
    >
      <div style={eyebrow}>Direction C · Today's Build</div>
      <h2
        style={{
          fontFamily: "var(--sa-font-display)",
          fontSize: 26,
          fontWeight: 600,
          marginTop: 12,
          color: "var(--sa-fg-primary)",
        }}
      >
        Today's slip is in adversarial review
      </h2>
      <p
        style={{
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.6,
          marginTop: 12,
          maxWidth: 720,
        }}
      >
        The Super Acca only publishes once the slip clears the contradiction
        engine, the adversarial review, and meets the tier confidence floor.
        If conviction isn't there by 11:30 GMT, no slip is published —
        STRIKZA never invents a fabricated tip.
      </p>
    </div>
  );
}
